/**
 * SummonSystem — pure lifespan-tracking logic for temporary allies.
 * No Phaser imports — fully testable in Node.js.
 *
 * The StageScene owns the Phaser game objects; this module owns the lifespans
 * and ID assignment so the scene can map IDs to entities for despawn.
 */

import type { AllyKind } from '@/data/backupSummons';

export interface AllyRecord {
  readonly id: number;
  readonly kind: AllyKind;
  /** Milliseconds remaining before this ally despawns. */
  remainingMs: number;
}

export interface SummonState {
  allies: AllyRecord[];
  nextId: number;
}

export function createSummonState(): SummonState {
  return { allies: [], nextId: 0 };
}

/**
 * Add `count` allies of `kind` with `lifespanMs` countdown.
 * Returns the newly created records (IDs are unique within the state).
 */
export function addAllies(
  state: SummonState,
  kind: AllyKind,
  count: number,
  lifespanMs: number,
): AllyRecord[] {
  const created: AllyRecord[] = [];
  for (let i = 0; i < count; i++) {
    const rec: AllyRecord = { id: state.nextId++, kind, remainingMs: lifespanMs };
    state.allies.push(rec);
    created.push(rec);
  }
  return created;
}

/**
 * Advance all ally lifespans by `deltaMs`.
 * Returns the IDs of allies whose time expired (they are removed from state).
 */
export function tickAllies(state: SummonState, deltaMs: number): number[] {
  const expired: number[] = [];
  state.allies = state.allies.filter((a) => {
    a.remainingMs -= deltaMs;
    if (a.remainingMs <= 0) {
      expired.push(a.id);
      return false;
    }
    return true;
  });
  return expired;
}

/**
 * Remove a specific ally by ID (e.g. when KO'd before lifespan ends).
 * No-op if not found.
 */
export function removeAlly(state: SummonState, id: number): void {
  state.allies = state.allies.filter((a) => a.id !== id);
}

/**
 * Returns the index of the nearest target to (selfX, selfY), or -1 if empty.
 * Uses squared distance — safe for comparison, avoids sqrt.
 */
export function findNearestTargetIndex(
  selfX: number,
  selfY: number,
  targets: ReadonlyArray<{ readonly x: number; readonly y: number }>,
): number {
  if (targets.length === 0) return -1;
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < targets.length; i++) {
    const dx = targets[i].x - selfX;
    const dy = targets[i].y - selfY;
    const dist = dx * dx + dy * dy;
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return bestIdx;
}
